import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, ButtonGroup, Tooltip, Typography } from '@material-ui/core';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import SettingsIcon from '@material-ui/icons/Settings';
import WarningIcon from '@material-ui/icons/Warning';
import { PROPERTY_GROUP_VIEW_MODES } from '../hooks/useOperationalPropertyGroup';

const useStyles = makeStyles(theme => ({
  banner: {
    minHeight: 52,
    padding: '7px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#eef5ff',
    borderBottom: '1px solid #c6daf7',
    color: '#153e75',
    flexShrink: 0,
  },
  icon: {
    color: '#1976d2',
    flexShrink: 0,
  },
  identity: {
    minWidth: 0,
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.25,
  },
  detail: {
    fontSize: 12,
    color: '#52677f',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  warning: {
    color: '#ed6c02',
    fontSize: 18,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  modeButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 12px',
  },
  utilityButton: {
    textTransform: 'none',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

const memberName = member => (
  member?.propertyName || member?.name || member?.jobSiteName || 'Unnamed property'
);

export default function OperationalPropertyGroupBanner({ controller, onManage }) {
  const classes = useStyles();
  const { group, workspace, viewMode, setViewMode, fitToGroup } = controller || {};
  const members = workspace?.group?.members || group?.members || [];
  const areas = workspace?.areas || [];
  const warnings = workspace?.warnings || group?.warnings || [];
  const crossCustomerWarning = warnings.find(warning => warning.code === 'CROSS_CUSTOMER_GROUP');
  const memberSummary = useMemo(
    () => members.map(memberName).join(' · '),
    [members]
  );

  if (!group) return null;

  return (
    <div className={classes.banner} data-testid="operational-property-group-banner">
      <AccountTreeIcon className={classes.icon} />
      <div className={classes.identity}>
        <div className={classes.titleRow}>
          <Typography className={classes.title}>
            Property group: {group.name || 'Property group'}
          </Typography>
          {crossCustomerWarning && (
            <Tooltip title={crossCustomerWarning.message || 'This group includes different customers'}>
              <WarningIcon className={classes.warning} />
            </Tooltip>
          )}
        </div>
        <Tooltip title={memberSummary || ''}>
          <Typography className={classes.detail}>
            {members.length} {members.length === 1 ? 'property' : 'properties'}
            {' · '}
            {areas.length} shared {areas.length === 1 ? 'area' : 'areas'}
            {memberSummary ? ` · ${memberSummary}` : ''}
          </Typography>
        </Tooltip>
      </div>

      <div className={classes.controls}>
        <ButtonGroup size="small" aria-label="Property group view">
          <Button
            className={classes.modeButton}
            color="primary"
            variant={viewMode === PROPERTY_GROUP_VIEW_MODES.COMBINED ? 'contained' : 'outlined'}
            onClick={() => setViewMode(PROPERTY_GROUP_VIEW_MODES.COMBINED)}
          >
            Combined
          </Button>
          <Button
            className={classes.modeButton}
            color="primary"
            variant={viewMode === PROPERTY_GROUP_VIEW_MODES.SPLIT ? 'contained' : 'outlined'}
            onClick={() => setViewMode(PROPERTY_GROUP_VIEW_MODES.SPLIT)}
          >
            Split by property
          </Button>
        </ButtonGroup>
        <Button
          className={classes.utilityButton}
          size="small"
          startIcon={<CenterFocusStrongIcon />}
          onClick={fitToGroup}
        >
          Fit group
        </Button>
        <Button
          className={classes.utilityButton}
          size="small"
          startIcon={<SettingsIcon />}
          onClick={onManage}
        >
          Manage
        </Button>
      </div>
    </div>
  );
}
